<template lang="html">
  <div class="map-container" ref="map-container">
    <div>
      <svg :width="containerWidth" :height="containerHeight" ref="svg">
        <g v-if="geo">
          <g id="non-mra">
            <path
              v-for="feature in features.filter(f => !f.properties.mra)"
              :key="feature.properties.GM_NAAM"
              :d="getPath(feature)"
              stroke="#dddddd"
              stroke-width="1"
              :fill="getColor(feature)"
            ></path>
          </g>
          <g id="mra">
            <path
              v-for="feature in features.filter(f => f.properties.mra)"
              :key="feature.properties.GM_NAAM"
              :d="getPath(feature)"
              stroke="#ffffff"
              stroke-width="0.5"
              :fill="getColor(feature)"
              @mouseover="onMouseOver(feature, $event)"
              @mouseout="onMouseOut($event)"
            ></path>
          </g>
        </g>
        <g
          v-if="hoveredFeature"
          :transform="
            `translate(${hoverPanelLocation[0]},${hoverPanelLocation[1]})`
          "
          class="hover-panel"
        >
          <rect
            :width="hoverPanelSize.width"
            :height="hoverPanelSize.height"
            fill="#ffffff"
            opacity="0.8"
          ></rect>
          <g transform="translate(5,18)">
            <text font-size="14">
              {{ hoveredFeature.properties.GM_NAAM }}
            </text>
            <text font-size="18" font-weight="bold" y="21">
              {{ mapping[hoveredFeature.properties.GM_NAAM] }}
            </text>
            <text font-size="14" font-weight="bold" y="42">
              {{
                getValueLabel(
                  values[mapping[hoveredFeature.properties.GM_NAAM]]
                )
              }}
            </text>
          </g>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
import { geoPath, geoMercator } from "d3-geo";
import { max } from "d3-array";
import { getColorMap } from "../utils.js";
//import * as Topojson from 'topojson';

export default {
  name: "Map",
  data: () => ({
    containerWidth: 0,
    containerHeight: 0,
    minMapWidth: 500,
    hoveredFeature: null,
    hoverPanelSize: { width: 200, height: 70 }
  }),
  mounted() {
    this.containerWidth = Math.min(
      this.minMapWidth,
      this.$refs["map-container"].clientWidth
    );
    this.containerHeight = this.containerWidth / 1.2; //this.mapRatio; // * this.$refs["map-container"].clientHeight;
    this.containerX = this.$refs["map-container"].getBoundingClientRect().x;
    this.containerY = this.$refs["map-container"].getBoundingClientRect().y;
  },
  created() {
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  props: {
    values: Object,
    normalization: String
  },
  computed: {
    data() {
      return this.$store.getters.data;
    },
    geo() {
      //this is topojson
      return this.$store.getters.geo;
    },
    boundingBox() {
      //return this.geo == null ? [] : this.geo.bbox;
      return [4.4, 52.1, 5.7, 52.7];
    },
    mapRatio() {
      //map width / height
      const bboxProjLowerLeft = this.projection([
        this.boundingBox[0],
        this.boundingBox[1]
      ]);
      const bboxProjUpperRight = this.projection([
        this.boundingBox[2],
        this.boundingBox[3]
      ]);
      return (
        (bboxProjUpperRight[0] - bboxProjLowerLeft[0]) /
        (bboxProjLowerLeft[1] - bboxProjUpperRight[1])
      );
    },
    center() {
      return this.boundingBox.length != 4
        ? []
        : [
            (this.boundingBox[0] + this.boundingBox[2]) / 2,
            (this.boundingBox[1] + this.boundingBox[3]) / 2
          ];
    },
    features() {
      //we need geojson features
      //return this.geo == null ? [] : Topojson.feature(this.geo, this.geo.objects.data).features;
      return this.geo == null ? [] : this.geo.features;
    },
    projection() {
      let scale = 1000; //initial scale
      const proj = geoMercator()
        .scale(scale)
        .translate([this.containerWidth / 2, this.containerHeight / 2])
        .center(this.center);
      const bboxProjLowerLeft = proj([
        this.boundingBox[0],
        this.boundingBox[1]
      ]);
      const bboxProjUpperRight = proj([
        this.boundingBox[2],
        this.boundingBox[3]
      ]);
      const projWidth = bboxProjUpperRight[0] - bboxProjLowerLeft[0];
      const projHeight = bboxProjLowerLeft[1] - bboxProjUpperRight[1];
      const factor = Math.min(
        this.containerWidth / projWidth,
        this.containerHeight / projHeight
      );
      scale *= factor;
      proj.scale(scale);
      return proj;
    },
    maxValue() {
      return max(Object.values(this.values));
    },
    colorMap() {
      return getColorMap(this.maxValue);
    },
    mapping() {
      return this.$store.getters.mapping;
    },
    hoverPanelLocation() {
      if (!this.hoveredFeature) return [0, 0];
      else {
        const centroid = this.projection([
          this.hoveredFeature.properties.centroid_x,
          this.hoveredFeature.properties.centroid_y
        ]);
        const x =
          centroid[0] > this.containerWidth / 2
            ? centroid[0] - this.hoverPanelSize.width - 10
            : centroid[0] + 10;
        const y =
          centroid[1] > this.containerHeight / 2
            ? centroid[1] - this.hoverPanelSize.height - 10
            : centroid[1] + 10;
        return [x, y];
      }
    }
  },
  methods: {
    getColor(feature) {
      if (feature.properties.mra) {
        const key = this.mapping[feature.properties.GM_NAAM];
        const value = this.values[key];
        return isNaN(value) || value == 0 ? "#eeeeee" : this.colorMap(value);
      } else {
        //not part of mra
        return "#dddddd";
      }
    },
    getPath(feature) {
      return geoPath().projection(this.projection)(feature);
    },
    onMouseOver(feature, event) {
      event.target.parentElement.append(event.target); //bring path to front
      event.target.style.strokeWidth = 3;
      this.hoveredFeature = feature;
    },
    onMouseOut(event) {
      event.target.style.strokeWidth = 0.5;
      this.hoveredFeature = null;
    },
    getValueLabel(value) {
      if (isNaN(value)) return "--";
      if (this.normalization == "none" || value == 0) return value;
      return "1 op " + Math.round(1 / value) + " inwoners";
    },
    onResize() {
      this.containerWidth = Math.min(
        this.minMapWidth,
        this.$refs["map-container"].clientWidth
      );
      this.containerHeight = this.containerWidth / 1.2; //this.mapRatio; // * this.$refs["map-container"].clientHeight;
    }
  }
};
</script>

<style lang="css" scoped>
.main-container {
  padding: 20px;
  height: 100%;
}
.map-container {
  height: 100%;
  text-align: center;
}
.hover-panel {
  pointer-events: none;
}
.detail-title {
  font-size: 1.2em;
  font-weight: bold;
}
.detail-subtitle {
  font-size: 1em;
}
.detail-aantal {
  font-size: 1.2em;
  font-weight: bold;
}
</style>
